(ns rothko.editor.document.script
  (:require [clojure.string :as string]
            [com.rpl.specter :as sp]))

(defn selection
  [start-index start-offset end-index end-offset]
  {:start-index start-index
   :start-offset start-offset
   :end-index end-index
   :end-offset end-offset})

(defn caret
  [index offset]
  (selection index offset index offset))


(defn- token-positions
  "Returns a sequence of the index in the text of the start of each token,
   followed by the index at the end of the text (where the next token would be if it existed)"
  [script skip?-fn skip-succeeding-space?]
  (loop [script script index 0 result [] skip1? false]
    (if (empty? script)
      (conj result index)
      (let [this-token (first script)
            skip? (or skip1? (and skip?-fn (skip?-fn this-token)))]
        (recur (rest script)
               (if skip? index (+ index (count (:word this-token))))
               (conj result index)
               (and skip? (not skip1?) skip-succeeding-space?))))))


(defn- offset->token
  "Converts a document offset into a token index and offset"
  [bias positions index]
  (let [clipped (-> index (max 0) (min (last positions)))
        tokens-before (take-while #((case bias :start <= :end <) % clipped) positions)
        token-index (-> (count tokens-before) (dec) (max 0))]
    [token-index (- clipped (get positions token-index))]))

(defn- token->offset
  "Converts a token index and offset into a document"
  [positions index offset]
  (let [clamp-value (if (neg? index) 0 (last positions))]
    (min (+ (nth positions index clamp-value) offset)
         (nth positions (inc index) clamp-value))))


(defn document-offsets->selection
  "Given a script and document offsets, and optionally a token-skipping predicate and post-token-skip space-skip flag,
   determine the selection that the document offsets translate to."
  ([script offsets] (document-offsets->selection script offsets (constantly false) false))
  
  ([script
    {:keys [document-start-offset document-end-offset] :as _document-offsets}
    skip?-fn
    skip-succeeding-space?]

   (let [positions (token-positions script skip?-fn skip-succeeding-space?)]
     (if (= document-start-offset document-end-offset)
       (let [[index offset] (offset->token :start positions document-start-offset)
             [index offset] (if (and (zero? offset)
                                     (or (-> script (get (dec index) {:type :space}) :type #{:word :edit})
                                         (-> script (get index {:type :space}) :type #{:space :space-edit})))
                              (offset->token :end positions document-start-offset)
                              [index offset])]
         {:start-index index
          :start-offset offset
          :end-index index
          :end-offset offset})
       (let [[token-at-start start-offset] (offset->token :start positions document-start-offset)
             [token-at-end end-offset] (offset->token :end positions document-end-offset)]
         {:start-index token-at-start, :start-offset start-offset, :end-index token-at-end, :end-offset end-offset})))))


(defn selection->document-offsets
  "Given a document, and optionally a token-skipping predicate and post-token-skip space-skip flag,
   determine the document offsets of the current selection."
  ([document] (selection->document-offsets document (constantly false) false))

  ([{{:keys [start-index start-offset end-index end-offset]} :selection :keys [script] :as _document}
    skip?-fn
    skip-succeeding-space?]
   (let [positions (token-positions script skip?-fn skip-succeeding-space?)
         document-start-offset (token->offset positions start-index start-offset)
         document-end-offset (token->offset positions end-index end-offset)
         offsets {:document-start-offset document-start-offset, :document-end-offset document-end-offset}]
     offsets)))

(def fix-intermediate-selection
  ^{:doc "Adjusts intermediate selection between batched transformations, so it is consistent with the
  selection read from the browser, as if the transformations were performed one at a time, interactively.
  In practice, transformations can only be batched when there are editor performance issues,
  which should be very rare, but potentially the inconsistency can lead to bugs (see ELIT-2920)."}
  (sp/terminal
   (fn [document]
     (assoc document :selection (document-offsets->selection (:script document) (selection->document-offsets document))))))

(defn apply-transformations!
  [!document transformations]
  (when-not (empty? transformations)
    (sp/multi-transform
     [sp/ATOM (reduce sp/multi-path (interpose fix-intermediate-selection transformations))]
     !document)))

(defn assoc-suspicious
  [{:keys [score] :as token}]
  (assoc token :suspicious? (not (or (nil? score) (pos? score)))))

(defn detect-contraction
  [{this-type :type} next-token]
  (and (#{:edit, :word} this-type)
       (some-> next-token :word (string/lower-case) (= "n't"))))
